<script setup>
// MANGO OFFICE inbox settings: connection diagnostics, the one manual step
// (pasting the notification URL into the MANGO panel — there is no API for it)
// and agent ↔ PBX employee mapping.
//
// Mapping is normally done by the auto-setup on connect; this page exists to
// finish whoever didn't match by email and to see what the PBX actually reports.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MangoAPI from 'dashboard/api/mango';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();

const checks = ref([]);
const isChecking = ref(false);
const isMatching = ref(false);
const webhookUrl = ref('');
const webhookSeen = ref({});
const employees = ref([]);
const agents = ref([]);

// The events we actually act on. `call` and `summary` are the call lifecycle;
// recording only matters once call recording is enabled on the account.
const WEBHOOK_EVENTS = [
  { key: 'call', required: true },
  { key: 'summary', required: true },
  { key: 'recording', required: false },
];

const inboxId = computed(() => props.inbox.id);

const audioMode = computed(() => props.inbox.audio_mode || 'cti');

// Nothing has ever arrived → the address is almost certainly not saved in the
// MANGO panel yet, which is the single most common reason calls never show up.
const noEventsYet = computed(() => Object.keys(webhookSeen.value).length === 0);

const statusClass = status => {
  if (status === 'ok') return 'bg-n-teal-9';
  if (status === 'error') return 'bg-n-ruby-9';
  return 'bg-n-amber-9';
};

const seenAt = key => {
  const timestamp = webhookSeen.value[key];
  if (!timestamp) return null;
  return new Date(timestamp * 1000).toLocaleString();
};

const loadDiagnostics = async () => {
  isChecking.value = true;
  try {
    const { data } = await MangoAPI.diagnostics(inboxId.value);
    checks.value = data.checks || [];
  } catch (error) {
    useAlert(t('INBOX_MGMT.MANGO.DIAGNOSTICS_ERROR'));
  } finally {
    isChecking.value = false;
  }
};

const loadWebhookStatus = async () => {
  const { data } = await MangoAPI.webhookStatus(inboxId.value);
  webhookUrl.value = data.webhook_url || '';
  webhookSeen.value = data.events || {};
};

const loadAgents = async () => {
  const [{ data: agentData }, { data: directoryData }] = await Promise.all([
    MangoAPI.agents(inboxId.value),
    MangoAPI.directory(inboxId.value),
  ]);
  // Remember what is actually stored, so the row can show only the action that
  // makes sense right now: save an unsaved change, or unlink an existing one.
  agents.value = (agentData.agents || []).map(agent => ({
    ...agent,
    savedExtension: agent.extension || null,
  }));
  employees.value = directoryData.employees || [];
};

const employeeLabel = employee => {
  const parts = [`${employee.extension} — ${employee.name}`];
  if (employee.department) parts.push(`(${employee.department})`);
  return parts.join(' ');
};

// Saving the extension is what makes the agent callable: it is the id MANGO
// acts on for outbound commands and reports back on inbound legs.
const saveAgent = async agent => {
  const employee = employees.value.find(e => e.extension === agent.extension);
  try {
    await MangoAPI.updateAgent(inboxId.value, {
      user_id: agent.user_id,
      extension: agent.extension,
      mango_user_id: employee?.mango_user_id,
      sip_login: employee?.sip_login,
    });
    agent.savedExtension = agent.extension;
    useAlert(t('INBOX_MGMT.MANGO.AGENT_SAVED'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.MANGO.AGENT_SAVE_ERROR'));
  }
};

// Browser audio needs a line: MANGO never reveals existing passwords, so we set
// a new one on the employee's own SIP account and keep it here.
const provisionSip = async agent => {
  try {
    const { data } = await MangoAPI.provisionSip(inboxId.value, agent.user_id);
    agent.sip_login = data.sip_login;
    agent.has_sip_password = true;
    useAlert(t('INBOX_MGMT.MANGO.AGENTS.LINE_ISSUED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('INBOX_MGMT.MANGO.AGENTS.LINE_FAILED')
    );
  }
};

const clearAgent = async agent => {
  await MangoAPI.deleteAgent(inboxId.value, agent.user_id);
  agent.extension = null;
  agent.savedExtension = null;
};

// Same routine the connect wizard runs — re-matching by email after new agents
// join the inbox, without touching mappings that were corrected by hand.
const autoMatch = async () => {
  isMatching.value = true;
  try {
    const { data } = await MangoAPI.setup(inboxId.value);
    const step = (data.steps || []).find(s => s.key === 'agents');
    useAlert(step?.message || t('INBOX_MGMT.MANGO.AUTO_MATCH_DONE'));
    await loadAgents();
  } catch (error) {
    useAlert(t('INBOX_MGMT.MANGO.AUTO_MATCH_ERROR'));
  } finally {
    isMatching.value = false;
  }
};

const copyWebhookUrl = () => {
  navigator.clipboard?.writeText(webhookUrl.value);
  useAlert(t('INBOX_MGMT.MANGO.WEBHOOK.COPIED'));
};

// MANGO allows one employee-list request per two seconds, and both the agent
// table and the diagnostics need it — firing them together made the checklist
// report "no employees" while the table right below listed them.
onMounted(async () => {
  await loadWebhookStatus();
  await loadAgents();
  await loadDiagnostics();
});
</script>

<template>
  <div class="flex flex-col gap-5 py-4 max-w-4xl">
    <!-- Connection -->
    <section class="rounded-xl border border-n-weak bg-n-solid-1">
      <header
        class="flex items-center justify-between gap-4 px-5 py-4 border-b border-n-weak"
      >
        <div>
          <h4 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.MANGO.DIAGNOSTICS.TITLE') }}
          </h4>
          <p class="mt-1 mb-0 text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.MANGO.DIAGNOSTICS.DESC') }}
          </p>
        </div>
        <NextButton
          faded
          blue
          sm
          :is-loading="isChecking"
          :label="$t('INBOX_MGMT.MANGO.DIAGNOSTICS.RECHECK')"
          @click="loadDiagnostics"
        />
      </header>
      <dl class="grid grid-cols-1 gap-px m-0 bg-n-weak sm:grid-cols-2">
        <div
          v-for="check in checks"
          :key="check.key"
          class="flex items-start gap-2.5 px-5 py-3 bg-n-solid-1"
        >
          <span
            class="mt-1.5 rounded-full size-2 shrink-0"
            :class="statusClass(check.status)"
          />
          <div class="min-w-0">
            <dt class="text-sm text-n-slate-12">
              {{
                $t(
                  `INBOX_MGMT.ADD.MANGO_CHANNEL.CHECKS.${check.key.toUpperCase()}`
                )
              }}
            </dt>
            <dd class="m-0 text-xs text-n-slate-11 break-words">
              {{ check.message }}
            </dd>
          </div>
        </div>
      </dl>
    </section>

    <!-- Notification address: the one step MANGO has no API for -->
    <section class="rounded-xl border border-n-weak bg-n-solid-1">
      <header class="px-5 py-4 border-b border-n-weak">
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.MANGO.WEBHOOK.TITLE') }}
        </h4>
        <p class="mt-1 mb-0 text-xs text-n-slate-11 max-w-2xl">
          {{ $t('INBOX_MGMT.MANGO.WEBHOOK.DESC') }}
        </p>
      </header>
      <div class="px-5 py-4">
        <div class="flex items-center gap-2">
          <input
            :value="webhookUrl"
            readonly
            class="flex-1 mb-0 font-mono text-xs"
          />
          <NextButton
            faded
            blue
            sm
            :label="$t('INBOX_MGMT.MANGO.WEBHOOK.COPY')"
            @click="copyWebhookUrl"
          />
        </div>
        <div class="p-3 mt-3 rounded-lg bg-n-alpha-1">
          <p class="mb-2 text-xs font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.MANGO.WEBHOOK.FLAGS_TITLE') }}
          </p>
          <ul class="pl-4 mb-2 text-xs list-disc text-n-slate-11 space-y-1">
            <li>{{ $t('INBOX_MGMT.MANGO.WEBHOOK.FLAG_SIP_CALL_ID') }}</li>
            <li>{{ $t('INBOX_MGMT.MANGO.WEBHOOK.FLAG_RECORDING') }}</li>
            <li>{{ $t('INBOX_MGMT.MANGO.WEBHOOK.FLAG_RECORD_ADDED') }}</li>
          </ul>
          <p class="mb-0 text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.MANGO.WEBHOOK.FLAGS_REST') }}
          </p>
        </div>

        <div class="flex flex-wrap gap-x-6 gap-y-2 mt-4">
          <div
            v-for="event in WEBHOOK_EVENTS"
            :key="event.key"
            class="flex items-center gap-2 text-xs"
          >
            <span
              class="rounded-full size-2 shrink-0"
              :class="statusClass(seenAt(event.key) ? 'ok' : 'warn')"
            />
            <span class="text-n-slate-12">
              {{
                $t(`INBOX_MGMT.MANGO.WEBHOOK.EVENTS.${event.key.toUpperCase()}`)
              }}
            </span>
            <span class="text-n-slate-11">
              {{ seenAt(event.key) || $t('INBOX_MGMT.MANGO.WEBHOOK.NOT_SEEN') }}
            </span>
          </div>
        </div>
        <p
          v-if="noEventsYet"
          class="p-3 mt-4 mb-0 text-xs leading-relaxed rounded-lg bg-n-amber-3 text-n-amber-12"
        >
          {{ $t('INBOX_MGMT.MANGO.WEBHOOK.NOTHING_YET') }}
        </p>
      </div>
    </section>

    <!-- Agents -->
    <section class="rounded-xl border border-n-weak bg-n-solid-1">
      <header
        class="flex items-center justify-between gap-4 px-5 py-4 border-b border-n-weak"
      >
        <div>
          <h4 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.MANGO.AGENTS.TITLE') }}
          </h4>
          <p class="mt-1 mb-0 text-xs text-n-slate-11 max-w-2xl">
            {{ $t('INBOX_MGMT.MANGO.AGENTS.DESC') }}
          </p>
        </div>
        <NextButton
          faded
          blue
          sm
          :is-loading="isMatching"
          :label="$t('INBOX_MGMT.MANGO.AGENTS.AUTO_MATCH')"
          @click="autoMatch"
        />
      </header>

      <table class="w-full text-sm border-collapse">
        <thead>
          <tr class="text-xs text-n-slate-11">
            <th class="px-5 py-2 font-normal text-left">
              {{ $t('INBOX_MGMT.MANGO.AGENTS.COL_AGENT') }}
            </th>
            <th class="px-3 py-2 font-normal text-left">
              {{ $t('INBOX_MGMT.MANGO.AGENTS.COL_EMPLOYEE') }}
            </th>
            <th class="px-3 py-2 font-normal text-left w-40">
              {{ $t('INBOX_MGMT.MANGO.AGENTS.COL_LINE') }}
            </th>
            <th class="px-5 py-2 font-normal text-right w-44" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="agent in agents"
            :key="agent.user_id"
            class="border-t border-n-weak"
          >
            <td class="px-5 py-3 align-middle">
              <div class="text-n-slate-12">{{ agent.name }}</div>
              <div class="text-xs text-n-slate-11">{{ agent.email }}</div>
            </td>
            <td class="px-3 py-3 align-middle">
              <select v-model="agent.extension" class="mb-0 w-full max-w-xs">
                <option :value="null">
                  {{ $t('INBOX_MGMT.MANGO.AGENTS.NOT_MAPPED') }}
                </option>
                <option
                  v-for="employee in employees"
                  :key="employee.extension"
                  :value="employee.extension"
                >
                  {{ employeeLabel(employee) }}
                </option>
              </select>
            </td>
            <td class="px-3 py-3 align-middle text-xs">
              <span v-if="agent.has_sip_password" class="text-n-teal-11">
                {{ agent.sip_login }}
              </span>
              <span v-else-if="audioMode === 'bridge'" class="text-n-slate-11">
                {{ $t('INBOX_MGMT.MANGO.AGENTS.LINE_NONE') }}
              </span>
              <span v-else class="text-n-slate-11">—</span>
            </td>
            <td class="px-5 py-3 align-middle text-right whitespace-nowrap">
              <NextButton
                v-if="agent.extension && agent.extension !== agent.savedExtension"
                faded
                blue
                sm
                :label="$t('INBOX_MGMT.MANGO.AGENTS.SAVE')"
                @click="saveAgent(agent)"
              />
              <template v-else-if="agent.savedExtension">
                <NextButton
                  v-if="audioMode === 'bridge' && !agent.has_sip_password"
                  faded
                  blue
                  sm
                  class="mr-2"
                  :label="$t('INBOX_MGMT.MANGO.AGENTS.ISSUE_LINE')"
                  @click="provisionSip(agent)"
                />
                <NextButton
                  faded
                  ruby
                  sm
                  :label="$t('INBOX_MGMT.MANGO.AGENTS.CLEAR')"
                  @click="clearAgent(agent)"
                />
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <p
        v-if="audioMode === 'bridge'"
        class="px-5 py-4 mb-0 text-xs leading-relaxed border-t border-n-weak text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.MANGO.AUDIO_MODE.BRIDGE_WARNING') }}
      </p>
    </section>
  </div>
</template>
